// Case studies for the featured projects. All facts come from the resume.
// Diagrams are drawn from this data by components/Dev/Diagram.js.
// Node kinds: "client" | "service" | "key" (the piece I led) | "data" | "external".
// Edge sides: t(op) r(ight) b(ottom) l(eft).

export const CASE_STUDIES = [
  {
    id: "marriott",
    company: "Marriott International",
    role: "Senior Full Stack Engineer",
    period: "Oct 2024 – Sep 2026",
    title: "A single pane of glass for the hotel front desk",
    problem:
      "Front-desk teams had reservation, room, guest, loyalty, and service information spread across systems and screens. The goal was one application where a stay can be managed from start to finish.",
    diagram: {
      width: 760,
      height: 290,
      nodes: [
        { id: "app", label: "Front-desk app", sub: "Next.js · RSC · Socket.IO", x: 20, y: 40, w: 160, h: 54, kind: "client", note: "Guided check-in, checkout, room moves, guest profiles, and loyalty enrollment, with live updates over WebSockets." },
        { id: "ai", label: "AI-assisted search", sub: "natural language", x: 20, y: 170, w: 160, h: 54, kind: "service", note: "Turns questions into authorized searches and returns grounded summaries." },
        { id: "gw", label: "Apollo gateway", sub: "Federation", x: 235, y: 105, w: 130, h: 54, kind: "key", note: "One type-safe GraphQL interface over all hotel data." },
        { id: "s1", label: "Reservations", sub: "NestJS subgraph", x: 420, y: 10, w: 130, h: 48, kind: "service", note: "NestJS services as Apollo Federation subgraphs." },
        { id: "s2", label: "Rooms and guests", sub: "NestJS subgraph", x: 420, y: 90, w: 130, h: 48, kind: "service", note: "DataLoader batching prevents N+1 queries." },
        { id: "s3", label: "Loyalty and service", sub: "NestJS subgraph", x: 420, y: 170, w: 130, h: 48, kind: "service", note: "Runs as containers on Amazon ECS." },
        { id: "cache", label: "Redis", sub: "shared cache", x: 420, y: 238, w: 130, h: 44, kind: "data", note: "Caches frequently used reference data, speeding targeted high-traffic queries by up to 60%." },
        { id: "pms", label: "Property systems", sub: "and Salesforce", x: 610, y: 10, w: 130, h: 54, kind: "external", note: "Integrated Property Management Systems and Salesforce." },
        { id: "bus", label: "Kafka · SNS · SQS", sub: "events and retries", x: 610, y: 170, w: 130, h: 54, kind: "service", note: "Hotel-event streaming, fan-out, and reliable processing with retries." },
      ],
      edges: [
        { from: "app", fs: "r", to: "gw", ts: "l" },
        { from: "ai", fs: "r", to: "gw", ts: "l" },
        { from: "gw", fs: "r", to: "s1", ts: "l" },
        { from: "gw", fs: "r", to: "s2", ts: "l" },
        { from: "gw", fs: "r", to: "s3", ts: "l" },
        { from: "s1", fs: "r", to: "pms", ts: "l" },
        { from: "bus", fs: "l", to: "s3", ts: "r", dashed: true },
        { from: "s3", fs: "b", to: "cache", ts: "t", dashed: true },
      ],
    },
    decisions: [
      {
        title: "Federate the data, unify the experience",
        text: "NestJS services run as Apollo Federation subgraphs behind a shared gateway, giving the front end one type-safe interface to reservation, room, guest, and loyalty data.",
      },
      {
        title: "Fix the slow path, not everything",
        text: "Request-scoped DataLoader batching removed N+1 calls, and Redis cached shared reference data, improving response times by up to 60% on targeted high-traffic queries.",
      },
      {
        title: "Ground the AI in authorized data",
        ai: true,
        text: "Natural-language questions become authorized searches over reservation, room, and guest data, so staff get grounded summaries instead of hunting across screens.",
      },
      {
        title: "Keep systems in sync with events",
        text: "Kafka streams hotel events, SNS fans them out, and SQS handles reliable processing and retries between property systems and Salesforce.",
      },
    ],
    results: [
      { value: "60%", label: "faster responses on high-traffic queries" },
      { label: "Shorter check-in, checkout, and room-move tasks for front-desk staff" },
    ],
    ownership:
      "Built the platform end to end, from guided front-desk workflows to federated services, event-driven integrations, and AI-assisted search.",
  },
  {
    id: "uber",
    company: "Uber",
    role: "Senior Full Stack Engineer",
    period: "Apr 2022 – Oct 2024",
    title: "Booking that converts, and tracking riders trust",
    problem:
      "Two goals: turn more first-time riders into booked trips, and give riders confidence during an active trip. Alongside that, engineering teams needed a clear view of infrastructure cost and efficiency.",
    diagram: {
      width: 700,
      height: 300,
      labels: [
        { x: 20, y: 18, text: "RIDER EXPERIENCE" },
        { x: 20, y: 218, text: "INFRASTRUCTURE INSIGHTS" },
      ],
      nodes: [
        { id: "web", label: "Rider web app", sub: "React · Redux · TanStack", x: 20, y: 30, w: 160, h: 54, kind: "client", note: "Booking flow redesigned on the new design system, in a monorepo." },
        { id: "track", label: "Live ride tracking", sub: "Google Maps UI", x: 20, y: 130, w: 160, h: 54, kind: "client", note: "Interactive tracking that cut inbound support contacts during active trips by 22%." },
        { id: "svc", label: "Node.js services", sub: "GraphQL · gRPC", x: 245, y: 30, w: 150, h: 54, kind: "key", note: "Backend services for dispatch and driver-rider matching across a microservices architecture." },
        { id: "match", label: "Dispatch and matching", sub: "microservices", x: 455, y: 20, w: 225, h: 44, kind: "service", note: "gRPC methods and GraphQL queries and mutations." },
        { id: "price", label: "Pricing and recommendations", sub: "ML-powered APIs", x: 455, y: 90, w: 225, h: 44, kind: "external", note: "Surface relevant ride types, pickup times, and promotions." },
        { id: "tele", label: "Cloud cost telemetry", sub: "usage and spend", x: 20, y: 230, w: 160, h: 54, kind: "data", note: "Aggregated cloud usage and cost data." },
        { id: "jobs", label: "Python jobs", sub: "anomaly detection · forecasts", x: 245, y: 230, w: 150, h: 54, kind: "service", note: "Find unexpected spending patterns and generate forecasting data." },
        { id: "dash", label: "Insight dashboards", sub: "React · Chart.js · Grafana", x: 455, y: 230, w: 225, h: 54, kind: "client", note: "Help engineering teams investigate infrastructure inefficiencies earlier." },
      ],
      edges: [
        { from: "web", fs: "r", to: "svc", ts: "l" },
        { from: "web", fs: "b", to: "track", ts: "t" },
        { from: "svc", fs: "r", to: "match", ts: "l" },
        { from: "svc", fs: "r", to: "price", ts: "l" },
        { from: "tele", fs: "r", to: "jobs", ts: "l" },
        { from: "jobs", fs: "r", to: "dash", ts: "l" },
      ],
    },
    decisions: [
      {
        title: "Redesign the booking flow, then measure it",
        text: "Rebuilt the web booking experience on the new design system, which raised first-trip conversions 15% globally.",
      },
      {
        title: "Use ML where it helps the rider decide",
        ai: true,
        text: "Pricing and recommendation APIs surface relevant ride types, estimated pickup times, and promotions so riders compare options and book with confidence.",
      },
      {
        title: "Reduce anxiety during the trip",
        text: "An interactive Google Maps tracking UI cut inbound support contacts during active trips by 22%.",
      },
      {
        title: "Make infrastructure cost visible",
        ai: true,
        text: "Python jobs detect cost anomalies and forecast spend for dashboards, reducing manual analysis. Cloud Run services stayed above 99.9% availability with monitoring and alerts.",
      },
    ],
    results: [
      { value: "15%", label: "more first-trip conversions" },
      { value: "22%", label: "fewer support contacts" },
    ],
    ownership:
      "Revamped the rider booking experience, led the in-house infrastructure-efficiency platform, and mentored three junior engineers.",
  },
  {
    id: "walmart",
    company: "Walmart",
    role: "Senior Full Stack Engineer",
    period: "Jun 2019 – Mar 2022",
    title: "Membership and reviews that customers use more",
    problem:
      "Walmart+ needed a membership experience customers could manage themselves, and product reviews that people actually write and find useful. I built both end to end, from the interface to the services behind it.",
    diagram: {
      width: 700,
      height: 270,
      nodes: [
        { id: "web", label: "Membership web app", sub: "Next.js · React · TanStack Query", x: 20, y: 30, w: 180, h: 54, kind: "client", note: "Self-service flows and a personalized hub for benefits, promotions, and early access." },
        { id: "rev", label: "Review workflows", sub: "rich text · validation", x: 20, y: 150, w: 180, h: 54, kind: "client", note: "Accessible rich-text reviews with client- and server-side validation." },
        { id: "gql", label: "GraphQL layer", sub: "Node.js · resolvers", x: 255, y: 90, w: 150, h: 54, kind: "key", note: "Resolvers, queries, and mutations. TanStack Query adds caching, request deduplication, and background refresh on the client." },
        { id: "core", label: "Core services", sub: "membership · billing", x: 460, y: 20, w: 220, h: 50, kind: "external", note: "Fewer redundant calls reach these services thanks to client-side caching and deduplication." },
        { id: "py", label: "Review intelligence", sub: "Python · ML ranking and classification", x: 460, y: 95, w: 220, h: 54, kind: "service", note: "Ranks relevant reviews and flags abusive content automatically." },
        { id: "aws", label: "AWS workloads", sub: "EC2 · Lambda · S3 · DynamoDB", x: 460, y: 175, w: 220, h: 54, kind: "data", note: "Monitored with Grafana and Splunk for page latency, API failures, and availability." },
      ],
      edges: [
        { from: "web", fs: "r", to: "gql", ts: "l" },
        { from: "rev", fs: "r", to: "gql", ts: "l" },
        { from: "gql", fs: "r", to: "core", ts: "l" },
        { from: "gql", fs: "r", to: "py", ts: "l" },
        { from: "gql", fs: "r", to: "aws", ts: "l", dashed: true },
      ],
    },
    decisions: [
      {
        title: "Let members manage their own plan",
        text: "Self-service flows for annual-plan upgrades, pauses and cancellations, payment-method updates, and billing-date changes reduced customers' dependence on support teams.",
      },
      {
        title: "Test the plan-selection experience",
        text: "A personalized membership hub surfaces benefits, promotions, and early access, and A/B tests tuned plan selection to improve benefit discovery and conversion.",
      },
      {
        title: "Add intelligence behind the reviews",
        ai: true,
        text: "Python services bring ML ranking and content classification into the Node.js and GraphQL layer, so relevant reviews rise and abusive content is flagged automatically.",
      },
      {
        title: "Share the building blocks",
        text: "Reusable components in an Nx monorepo with Storybook and styled-components kept the UI consistent and sped up delivery. Jest, React Testing Library, and TestCafe guarded the key flows.",
      },
    ],
    results: [
      { value: "25%", label: "more reviews per customer" },
      { value: "18%", label: "more engagement with relevant reviews" },
    ],
    ownership:
      "Owned membership and engagement features end to end on the Walmart+ team, from the interface to the services behind it.",
  },
];
